import React, { FC, memo, useEffect, useMemo } from 'react'

import { useLazyQuery } from '@apollo/client'
import { Select } from 'antd'
import { useFormContext, Controller } from 'react-hook-form'

import { GET_STATES } from 'Const'
import { State } from 'Types'

interface Props {
  className?: string
  countryFieldName: string
  id?: string
  name: string
}

interface Data {
  states: State[]
}

const StateSelect: FC<Props> = ({ name, className, countryFieldName, ...props }) => {
  const { control, setValue, register, watch } = useFormContext()
  const [getStates, { data, loading }] = useLazyQuery<Data>(GET_STATES)
  const countryIsoCode = watch(countryFieldName)
  const value = watch(name)

  useEffect(() => {
    if (countryIsoCode) {
      void getStates({ variables: { countryIsoCode } })
    }
  }, [countryIsoCode, getStates])

  useEffect(() => {
    register({ name })
  }, [register, name])

  useEffect(() => {
    if (data) {
      const isPresent = data.states.find((state) => state.isoCode === value)
      if (!isPresent) setValue(name, null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.states])

  const options = useMemo(
    () =>
      data?.states.map((state) => ({
        value: state.isoCode,
        label: state.name,
      })) || [],
    [data?.states],
  )

  return (
    <Controller
      as={
        <Select
          showSearch
          className={`w-100 ${className}`}
          disabled={!data?.states.length}
          loading={loading}
          optionFilterProp="label"
          options={options}
          placeholder="Select your state"
          size="large"
          {...props}
        />
      }
      control={control}
      name={name}
    />
  )
}

export default memo(StateSelect)
